import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useTenantContext } from '../auth/useTenantContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

// ─── Schemas ──────────────────────────────────────────────────────────────────

export const PickListSummarySchema = z.object({
  id: z.number(),
  external_id: z.string(),
  warehouse_id: z.number(),
  status: z.string(),
  // display_status: backend-derived presentation status (unassigned / assigned /
  // in_progress / fulfilled / confirmed / cancelled / archived). Optional because
  // older API responses may not include it; falls back to raw status in the UI.
  display_status: z.string().optional(),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  confirmed_at: z.string().nullable(),
  splitting_mode: z.string().nullable(),
  priority: z.number().nullable(),
  wave: z.string().nullable(),
  created_by_email: z.string().nullable(),
  item_count: z.number(),
  qty_total: z.number(),
  distinct_locations: z.number(),
  // SP-8c §4 — ubicazioni distinte dove l'operatore ha effettivamente prelevato.
  distinct_locations_picked: z.number().optional().default(0),
  mission_count: z.number(),
  auto_split_pending: z.boolean(),
  // SP-6a Book head fields (nullable for legacy lists).
  booking_date: z.string().nullable().optional(),
  list_type: z.string().nullable().optional(),
  list_category: z.string().nullable().optional(),
  // Insieme DISTINTO degli item_type (V/S) degli articoli della distinta, come
  // stringa CSV (es. "S,V"). Permette alla colonna Tipologia di mostrare più
  // badge affiancati per le liste miste. Optional/nullable per back-compat
  // (response vecchi durante deploy progressivo) e fallback su list_category.
  item_types: z.string().nullable().optional(),
  // SP-6c — operatori assegnati (CSV email) per la colonna Magazziniere.
  operator_emails: z.string().nullable().optional(),
  // SP-8 follow-up — count delle righe inevase calcolato a livello backend.
  // Optional per back-compat (response vecchi durante deploy progressivo).
  // L'UI lo usa per evidenziare badge + row highlight quando > 0 e status
  // ∈ {fulfilled, confirmed}.
  partials_count: z.number().optional(),
  // bug_fixes_tpicking_development — colli effettivamente prelevati
  // (SUM(tasks.qty_picked) FILTER (status='done')). Optional per
  // back-compat. Mostrato come colonna "Colli prelevati" nella testa.
  qty_picked_total: z.number().optional(),
  // picking-indexes — soglia max colli/missione del magazzino della lista
  // (join warehouses nel board). Optional per back-compat; il badge ICM ricade
  // sul default 100 quando assente.
  max_qty_per_mission: z.number().optional(),
  // picklist-head-cliente-ean — testa livello 1: prima ragione sociale in
  // ordine di pick (MIN seq, null se assente) + N. titoli (COUNT DISTINCT ean13).
  // Optional/nullable per back-compat durante deploy progressivo.
  customer_name: z.string().nullable().optional(),
  distinct_ean_count: z.number().optional().default(0),
});
export type PickListSummary = z.infer<typeof PickListSummarySchema>;

export const PickListsListSchema = z.object({
  items: z.array(PickListSummarySchema),
  next_cursor: z.string().nullable(),
});
export type PickListsList = z.infer<typeof PickListsListSchema>;

// SP-6c — admin board listing (offset pagination + filtri + sort).
export const PickListBoardSchema = z.object({
  items: z.array(PickListSummarySchema),
  total: z.number(),
  page: z.number(),
  per_page: z.number(),
});
export type PickListBoard = z.infer<typeof PickListBoardSchema>;

export const PickListItemSchema = z.object({
  pick_list_item_id: z.number(),
  seq: z.number(),
  item_external_id: z.string().nullable(),
  location_code: z.string().nullable(),
  qty_required: z.number(),
  qty_picked: z.number(),
  // Riga in un'ubicazione che il magazzino ha dichiarato da non prelevare:
  // l'operatore non la vede nemmeno, ma il capo magazzino sì.
  excluded_from_picking: z.boolean().optional().default(false),
  status: z.string(),
  attrs_snapshot: z.record(z.unknown()).optional(),
  // SP-6a Book item fields (nullable for legacy rows).
  customer_code: z.string().nullable().optional(),
  customer_name: z.string().nullable().optional(),
  recipient_code: z.number().nullable().optional(),
  province: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  ean13: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  publisher: z.string().nullable().optional(),
  item_type: z.string().nullable().optional(),
  qty_ordered: z.number().nullable().optional(),
  qty_assigned: z.number().nullable().optional(),
  price: z.number().nullable().optional(),
  stock_qty: z.number().nullable().optional(),
  location_code_2: z.string().nullable().optional(),
  booking_date: z.string().nullable().optional(),
  tasks: z.array(z.object({
    task_id: z.number(),
    mission_id: z.number(),
    operator_user_id: z.number().nullable(),
    qty_assigned: z.number(),
    qty_picked: z.number().nullable(),
    force_pick: z.boolean(),
    retry_count: z.number(),
    status: z.string(),
    started_at: z.string().nullable(),
    completed_at: z.string().nullable(),
  })).optional(),
});
export type PickListItem = z.infer<typeof PickListItemSchema>;

/**
 * Una pick-list ha "parziali/inevasi" se almeno un item non è prelevato per intero
 * (`qty_picked < qty_required`, incluso lo zero-pick). Definizione canonica,
 * allineata al gate di conferma backend. NON usare i bucket `summary.lines_*`:
 * classificano lo zero-pick (task done con qty_picked=0) come `pending` →
 * falso negativo (vedi PickListExportService::loadItems).
 */
export function pickListHasPartials(items: PickListItem[]): boolean {
  return items.some((it) => (it.qty_picked ?? 0) < it.qty_required);
}

export const PickListMissionSchema = z.object({
  id: z.number(),
  status: z.string(),
  operator_user_id: z.number().nullable(),
  operator_email: z.string().nullable(),
  assigned_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  task_count: z.number(),
  tasks_done: z.number(),
});
export type PickListMission = z.infer<typeof PickListMissionSchema>;

export const PickListDetailSchema = z.object({
  pick_list: z.object({
    id: z.number(),
    external_id: z.string(),
    warehouse_id: z.number(),
    tenant_id: z.number(),
    source: z.string().nullable(),
    priority: z.number().nullable(),
    wave: z.string().nullable(),
    status: z.string(),
    display_status: z.string().optional(),
    created_at: z.string(),
    completed_at: z.string().nullable(),
    confirmed_at: z.string().nullable(),
    auto_split_pending: z.boolean(),
    // SP-6a Book head fields (nullable for legacy lists).
    booking_date: z.string().nullable().optional(),
    list_type: z.string().nullable().optional(),
    list_category: z.string().nullable().optional(),
    // Colli e imballo registrati dal capo magazziniere a fine prelievo.
    // `null` = non ancora contati, ed è diverso da 0: la lista non parte
    // finché non sono stati inseriti.
    colli: z.number().nullable().optional(),
    imballo: z.string().nullable().optional(),
    // Valorizzato = lista già inviata all'ERP ⇒ i due valori sono congelati.
    exported_at: z.string().nullable().optional(),
    // SP-5l: ERP destinatario (single client per pick-list). Used as the
    // "Cliente" grouping key in the pick-list accordion. Backend export
    // returns it (PickListExportService); optional for older payloads.
    recipient: z.string().nullable().optional(),
    summary: z.object({
      items: z.number(),
      qty_required_total: z.number(),
      qty_picked_total: z.number(),
      lines_done: z.number(),
      lines_partial: z.number(),
      lines_cancelled: z.number(),
    }),
  }),
  items: z.array(PickListItemSchema),
  missions: z.array(PickListMissionSchema),
});
export type PickListDetail = z.infer<typeof PickListDetailSchema>;

// ─── Filters + query-string builder ──────────────────────────────────────────

export interface PickListsListFilters {
  status?: string[];
  sort?: 'created_at' | 'completed_at';
  external_id_search?: string;
  cursor?: string;
  limit?: number;
  auto_split_pending?: boolean;  // NEW
}

export function buildPickListsQueryString(f: PickListsListFilters): string {
  const qs = new URLSearchParams();
  if (f.status && f.status.length > 0) qs.set('status', f.status.join(','));
  if (f.sort) qs.set('sort', f.sort);
  if (f.external_id_search) qs.set('external_id_search', f.external_id_search);
  if (f.cursor) qs.set('cursor', f.cursor);
  if (f.limit) qs.set('limit', String(f.limit));
  if (f.auto_split_pending) qs.set('auto_split_pending', 'true');
  return qs.toString();
}

// ─── Internal fetch helper ────────────────────────────────────────────────────

async function authedFetch(
  path: string,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<unknown> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

// ─── Query hooks ──────────────────────────────────────────────────────────────

export function usePickListsList(
  token: string | null,
  tenantCode: string | null,
  filters: PickListsListFilters,
) {
  const { warehouseId } = useTenantContext();
  const qs = buildPickListsQueryString(filters);
  return useQuery<PickListsList>({
    queryKey: ['admin', 'pick-lists', 'list', filters],
    queryFn: async () => PickListsListSchema.parse(
      await authedFetch(`/admin/pick-lists/exports?${qs}`, token!, tenantCode!, warehouseId),
    ),
    enabled: token !== null && tenantCode !== null,
  });
}

// ─── SP-6c: admin board listing (offset + filters + sort) ────────────────────

export interface PickListBoardFilters {
  status?: string[];
  external_id_search?: string;
  unassigned?: boolean;
  list_type?: string;
  operator_id?: number[];
  // SP-8c — filtro "Tipologia" sulla testa list_category (V/S).
  list_category?: string;
  // SP-8b — filtri globali cliente/articolo (ILIKE parziale lato backend).
  // I 3 campi cliente filtrano la LISTA via EXISTS su pick_list_items; ean13
  // è un EXISTS separato; il range data è sulla testa pl.booking_date.
  customer_code?: string;
  recipient_code?: string; // testo parziale — il backend fa cast dello SMALLINT
  customer_name?: string;
  ean13?: string;
  booking_date_from?: string; // YYYY-MM-DD
  booking_date_to?: string;   // YYYY-MM-DD
  // dashboard-kpi: "solo evase parzialmente" (partials_count > 0). Alimentato
  // dal deep-link della card "Evase parzialmente" in dashboard.
  partials_only?: boolean;
  /** Solo liste evase ferme perché senza colli/imballo. */
  awaiting_colli?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export function buildBoardQueryString(f: PickListBoardFilters): string {
  const qs = new URLSearchParams();
  if (f.status && f.status.length > 0) qs.set('status', f.status.join(','));
  if (f.external_id_search) qs.set('external_id_search', f.external_id_search);
  if (f.unassigned) qs.set('unassigned', 'true');
  if (f.list_type) qs.set('list_type', f.list_type);
  if (f.operator_id && f.operator_id.length > 0) qs.set('operator_id', f.operator_id.join(','));
  if (f.list_category) qs.set('list_category', f.list_category);
  if (f.customer_code) qs.set('customer_code', f.customer_code);
  if (f.recipient_code) qs.set('recipient_code', f.recipient_code);
  if (f.customer_name) qs.set('customer_name', f.customer_name);
  if (f.ean13) qs.set('ean13', f.ean13);
  if (f.booking_date_from) qs.set('booking_date_from', f.booking_date_from);
  if (f.booking_date_to) qs.set('booking_date_to', f.booking_date_to);
  if (f.partials_only) qs.set('partials_only', 'true');
  if (f.awaiting_colli) qs.set('awaiting_colli', 'true');
  if (f.sort) qs.set('sort', f.sort);
  if (f.order) qs.set('order', f.order);
  qs.set('page', String(f.page ?? 1));
  qs.set('per_page', String(f.per_page ?? 20));
  return qs.toString();
}

export function usePickListsBoard(
  token: string | null,
  tenantCode: string | null,
  filters: PickListBoardFilters,
) {
  const { warehouseId } = useTenantContext();
  const qs = buildBoardQueryString(filters);
  // bug_fixes_tpicking_development — il magazzino va passato come QUERY param
  // (non solo header): il backend filtra `pl.warehouse_id = ?` solo se presente
  // e la RLS pinna solo il tenant. Senza, la board mostrava le liste di TUTTI i
  // magazzini del tenant. warehouseId in queryKey → refetch al cambio magazzino.
  const qsWh = warehouseId != null ? `${qs}&warehouse_id=${warehouseId}` : qs;
  return useQuery<PickListBoard>({
    queryKey: ['admin', 'pick-lists', 'board', filters, warehouseId],
    queryFn: async () => PickListBoardSchema.parse(
      await authedFetch(`/admin/pick-lists/board?${qsWh}`, token!, tenantCode!, warehouseId),
    ),
    enabled: token !== null && tenantCode !== null,
  });
}

export function usePickListDetail(
  token: string | null,
  tenantCode: string | null,
  id: number | null,
) {
  const { warehouseId } = useTenantContext();
  return useQuery<PickListDetail>({
    queryKey: ['admin', 'pick-lists', 'detail', id],
    queryFn: async () => PickListDetailSchema.parse(
      await authedFetch(`/admin/pick-lists/${id}/export?detail=tasks`, token!, tenantCode!, warehouseId),
    ),
    enabled: token !== null && tenantCode !== null && id !== null,
    // SP-11b: auto-refresh mentre la lista è "viva" (operatore sta prelevando o
    // missioni in attesa di chiusura). Le azioni dell'operatore passano dal
    // voice-gateway e NON invalidano la cache della dashboard, quindi senza
    // questo polling il dettaglio resta fermo durante la sessione. Si ferma da
    // solo appena la lista raggiunge uno stato terminale (fulfilled/confirmed/
    // archived/cancelled/open). Niente polling in background (tab nascosta).
    refetchInterval: (query) =>
      query.state.data?.pick_list.status === 'in_progress' ? 5_000 : false,
  });
}

// ─── SP-6b: customers aggregated for a pick-list ──────────────────────────────

export const PickListCustomerSchema = z.object({
  customer_code: z.string(),
  customer_name: z.string().nullable(),
  recipient_code: z.number().nullable(),
  province: z.string().nullable(),
  city: z.string().nullable(),
  delivered_value: z.number(),
  delivered_copies: z.number(),
  booked_copies: z.number(),
  item_count: z.number(),
});
export type PickListCustomer = z.infer<typeof PickListCustomerSchema>;

const PickListCustomersResponse = z.object({ customers: z.array(PickListCustomerSchema) });

export function usePickListCustomers(
  token: string | null,
  tenantCode: string | null,
  id: number | null,
  /** SP-11b: poll while the list is live so delivered copies track the picks. */
  live = false,
) {
  const { warehouseId } = useTenantContext();
  return useQuery<PickListCustomer[]>({
    queryKey: ['admin', 'pick-lists', 'customers', id],
    queryFn: async () => PickListCustomersResponse.parse(
      await authedFetch(`/admin/pick-lists/${id}/customers`, token!, tenantCode!, warehouseId),
    ).customers,
    enabled: token !== null && tenantCode !== null && id !== null,
    refetchInterval: live ? 5_000 : false,
  });
}

// ─── dashboard-kpi: by-status counts ─────────────────────────────────────────

// dashboard-kpi — schema tollerante a backend "vecchi" / parsing partial.
// I 5 contatori hanno `default(0)` così il front mostra "0" (informativo)
// invece che `undefined` (UI con "—") quando il backend non ha ancora il
// nuovo response shape.
export const PickListCountsSchema = z.object({
  warehouse_id: z.number().nullable(),
  by_status: z.object({
    open:          z.number().optional().default(0),
    assigned:      z.number().optional().default(0),
    in_progress:   z.number().optional().default(0),
    fulfilled:     z.number().optional().default(0),
    confirmed:     z.number().optional().default(0),
    cancelled:     z.number().optional().default(0),
    archived:      z.number().optional().default(0),
    with_partials: z.number().optional().default(0),
    // Liste evase ferme perché senza colli/imballo: non vengono inviate a Book.
    awaiting_colli: z.number().optional().default(0),
  }),
});
export type PickListCounts = z.infer<typeof PickListCountsSchema>;

/**
 * dashboard-kpi — counts pick-list per status (open/in_progress/fulfilled/
 * confirmed) + with_partials. Usato dalle KPI card in Home dashboard.
 * Refresh ogni 60s (staleTime); React Query gestisce il cache per warehouse.
 */
export function usePickListCounts(
  token: string | null,
  tenantCode: string | null,
  warehouseId: number | null,
) {
  return useQuery<PickListCounts>({
    queryKey: ['admin', 'pick-lists', 'counts', warehouseId ?? 'tenant-total'],
    queryFn: async () => {
      const qs = warehouseId !== null ? `?warehouse_id=${warehouseId}` : '';
      const raw = await authedFetch(`/admin/pick-lists/counts${qs}`, token!, tenantCode!, warehouseId);
      return PickListCountsSchema.parse(raw);
    },
    enabled: token !== null && tenantCode !== null,
    // SP-8c — dati di stato operativo "quasi real-time": staleTime 0 (sempre
    // considerati scaduti) + refetch ogni 5s + al focus della finestra, così le
    // card si aggiornano entro pochi secondi invece dei ~10s+ percepiti prima.
    staleTime: 0,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
}

/** SP-6b — format an 8-char customer code as `xx-xx-xxxx`. */
export function formatCustomerCode(code: string | null | undefined): string {
  if (!code) return '—';
  if (code.length !== 8) return code;
  return `${code.slice(0, 2)}-${code.slice(2, 4)}-${code.slice(4)}`;
}

/** SP-6b — format an ISO date (YYYY-MM-DD) as dd/mm/yyyy. */
export function formatBookingDate(d: string | null | undefined): string {
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
}
